import { Edit as EditIcon } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  IconButton,
  Stack,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { filterAndSortImages } from "../../media/useFilteredAndOrderedImages";
import CancelButton from "../cancelButton/CancelButton";
import type { Image } from "../imagesPreview/ImagePreview";
import ImagesPreview from "../imagesPreview/ImagesPreview";
import ImagePreview from "./ImagePreview";

interface ImagesPickerDialogProps {
  open: boolean;
  isLoading: boolean;
  images: Image[];
  selectedImages: Pick<Image, "id">[];
  selectionLimit: number;
  search: string;
  requiredTags: string[] | undefined;
  tagFilterEnabled: boolean;
  onTagFilterChange: (enabled: boolean) => void;
  onSelectionChange: (images: Image[]) => void;
  onSearchChange: (search: string) => void;
  onClose: () => void;
}

const ImagesPickerDialog: FC<ImagesPickerDialogProps> = ({
  open,
  isLoading,
  images,
  selectedImages,
  selectionLimit,
  search,
  requiredTags,
  tagFilterEnabled,
  onTagFilterChange,
  onSelectionChange,
  onSearchChange,
  onClose,
}) => {
  const [detailedSelectedImages, setDetailedSelectedImages] = useState(
    selectedImages.map((i) => images.find((img) => i.id === img.id)).filter((i) => !!i),
  );

  useEffect(() => {
    setDetailedSelectedImages(selectedImages.map((i) => images.find((img) => i.id === img.id)).filter((i) => !!i));
  }, [images, selectedImages]);

  const filteredAndSortedImages = filterAndSortImages(images, search, detailedSelectedImages);

  const handleToggleImageSelection = (image: Image) => {
    if (detailedSelectedImages.find((i) => i.id === image.id)) {
      setDetailedSelectedImages(detailedSelectedImages.filter((i) => i.id !== image.id));
    } else if (!selectionLimit || detailedSelectedImages.length < selectionLimit) {
      setDetailedSelectedImages([...detailedSelectedImages, image]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <DialogTitle>Wybierz grafiki</DialogTitle>
        {requiredTags && requiredTags.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox checked={tagFilterEnabled} onChange={(_, checked) => onTagFilterChange(checked)} size="small" />
            }
            label={requiredTags.join(", ")}
            slotProps={{ typography: { variant: "body2" } }}
          />
        )}
      </Stack>
      <DialogContent>
        <ImagesPreview
          isLoading={isLoading}
          images={filteredAndSortedImages}
          selectedImages={detailedSelectedImages}
          search={search}
          onSearchChange={onSearchChange}
          onToggleImageSelection={handleToggleImageSelection}
        />
      </DialogContent>
      <DialogActions>
        <CancelButton
          onClick={() => {
            onClose();
          }}
        />
        <Button
          color="primary"
          onClick={() => {
            onSelectionChange(detailedSelectedImages);
            onClose();
          }}
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  readonly images: Image[];
  readonly isLoading: boolean;
  readonly label: string;
  readonly limit: number;
  readonly productIds?: string[];
  readonly selectedImages: Omit<Image, "tags" | "products" | "isPrivate" | "www" | "flavours">[];
  onSelectionChange: (images: Image[]) => void;
}

const ImagesPicker: FC<Props> = ({
  images,
  isLoading,
  label,
  limit,
  productIds,
  selectedImages,
  onSelectionChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tagFilterEnabled, setTagFilterEnabled] = useState(true);
  const requiredTags = ["16:9"];
  const imagesToDisplay = pickableImages(
    images,
    selectedImages,
    tagFilterEnabled ? requiredTags : undefined,
    productIds,
  );
  const [search, setSearch] = useState("");
  const ITEM_SIZE = 60;
  return (
    <Stack
      direction="column"
      sx={{
        alignItems: "stretch",
      }}
    >
      <FormLabel>{label}</FormLabel>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", height: ITEM_SIZE }}>
        {selectedImages.map((i) => (
          <ImagePreview key={i.id} image={i} size={ITEM_SIZE} />
        ))}
        <IconButton onClick={() => setDialogOpen(true)} aria-label="Edytuj wybrane grafiki">
          <EditIcon />
        </IconButton>
      </Stack>
      <ImagesPickerDialog
        images={imagesToDisplay}
        isLoading={isLoading}
        open={dialogOpen}
        selectedImages={selectedImages}
        selectionLimit={limit}
        search={search}
        onSelectionChange={onSelectionChange}
        onSearchChange={setSearch}
        onClose={() => setDialogOpen(false)}
        requiredTags={requiredTags}
        tagFilterEnabled={tagFilterEnabled}
        onTagFilterChange={setTagFilterEnabled}
      />
    </Stack>
  );
};

/**
 * Images offered in the picker: those with all the required tags, narrowed to the given products unless none of
 * them match. Already selected images are always included, so that they can be deselected even if they don't match.
 */
function pickableImages(
  images: Image[],
  selectedImages: Pick<Image, "id">[],
  requiredTags: string[] | undefined,
  productIds: string[] | undefined,
) {
  const matchingTags = images.filter((i) => (requiredTags ?? []).every((tag) => i.tags.includes(tag)));
  const matchingProducts = productIds
    ? matchingTags.filter((i) => i.products.some((p) => productIds.includes(p)))
    : matchingTags;
  const pickable = matchingProducts.length > 0 ? matchingProducts : matchingTags;
  const selectedIds = new Set(selectedImages.map((i) => i.id));
  const selectedNotPickable = images.filter((i) => selectedIds.has(i.id) && !pickable.includes(i));
  return [...selectedNotPickable, ...pickable];
}

export default ImagesPicker;
